'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, ehRedeBloqueada } from '@/lib/api/errors'
import { indexarPorCanal, navigationKeys, podeVerNavegacao } from '@/lib/navigation/navegacao'
import { useAuth } from '@/providers/AuthContext'
import { useNavigationJoined } from '@/providers/NavigationContext'
import { fetchNavigation } from '@/services/users/navigation'
import type { ChannelNavigation } from '@/types/navigation'

const VAZIO: ChannelNavigation[] = []

export interface NavigationSnapshotResult {
  canais: readonly ChannelNavigation[]
  porCanal: ReadonlyMap<string, ChannelNavigation>
}

/**
 * `GET /users/navigation` como `['users','navigation']`. Só sai com o gate triplo E
 * depois do ack do `enter_navigation` (`joined`): o join vem antes do retrato por
 * construção. O ao-vivo vem do socket (`useNavigationLive` patcha esta key).
 */
export function useNavigationSnapshot(): NavigationSnapshotResult {
  const { user } = useAuth()
  const joined = useNavigationJoined()
  const pode = podeVerNavegacao(user)

  const query = useQuery<ChannelNavigation[]>({
    queryKey: navigationKeys.snapshot,
    queryFn: async () => {
      try {
        return await fetchNavigation()
      } catch (err) {
        // 404 = back anterior à rota; 403 fora da rede bloqueada = grant revogado entre o
        // gate e a resposta. Os dois são ausência: sucesso vazio, sem retry. O 403 de REDE
        // propaga — o desbloqueio só refaz consulta em erro.
        if (err instanceof ApiError && (err.status === 404 || (err.status === 403 && !ehRedeBloqueada(err)))) {
          return []
        }
        throw err
      }
    },
    enabled: pode && joined,
    staleTime: 5 * 60_000,
  })

  // Gate fechado corta NA SAÍDA: o `data` sobrevive no cache entre remounts.
  const canais = pode ? (query.data ?? VAZIO) : VAZIO
  const porCanal = useMemo(() => indexarPorCanal(canais), [canais])
  return { canais, porCanal }
}
